<template>
  <eg-additional-collapse
    :value="isCollapsed"
    :label="config.title"
    @input="handleCollapseChange"
  >
    <div class="advanced-settings">
      <div
        v-for="group in config.groups"
        :key="group.id"
        class="advanced-settings__group"
        :data-testid="group.dataTestId"
      >
        <!-- Group header with info icon -->
        <div class="advanced-settings__group-header">
          <span class="paragraph-s semi-bold">{{ group.title }}</span>
          <eg-icon
            v-if="group.onboardingKey"
            icon="information"
            size="small"
            color="var(--color-text-neutral-on-white)"
            class="advanced-settings__info-icon"
            @click.native="handleOnboardingClick(group.onboardingKey)"
          />
        </div>

        <!-- Group items (toggles) -->
        <div class="advanced-settings__items">
          <div
            v-for="(item, index) in group.items"
            :key="index"
            class="advanced-settings__item align-items-center"
          >
            <eg-toggle
              :value="item.value"
              :checked-value="true"
              :unchecked-value="false"
              :disabled="item.disabled"
              @input="handleItemChange(item, $event)"
            />
            <div class="advanced-settings__item-label">
              <p class="paragraph-s mb-0">
                {{ getItemLabel(item) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </eg-additional-collapse>
</template>

<script>
import EgAdditionalCollapse from "@/components/global/eg_additional_collapse.vue";
import EgToggle from "@/components/global/eg/eg_toggle.vue";
import EgIcon from "@/components/global/eg/eg_icon.vue";

export default {
  name: "EgAdvancedSettings",
  components: {
    EgAdditionalCollapse,
    EgToggle,
    EgIcon,
  },
  props: {
    // Configuration for groups and items
    config: {
      type: Object,
      required: true,
      validator(config) {
        return (
          config.title &&
          Array.isArray(config.groups) &&
          config.groups.every(
            (group) =>
              group.id &&
              group.title &&
              Array.isArray(group.items) &&
              group.items.every((item) => item.field && item.label)
          )
        );
      },
    },
    // Update field method from parent form
    updateField: {
      type: Function,
      required: true,
    },
    // .sync for collapsed state
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCollapsed: this.collapsed,
    };
  },
  watch: {
    collapsed(newVal) {
      this.isCollapsed = newVal;
    },
  },
  methods: {
    /**
     * Get item label with disabled message if applicable
     */
    getItemLabel(item) {
      if (item.disabled && item.disabledMessage) {
        return item.disabledMessage;
      }
      return item.label;
    },

    /**
     * Handle toggle change - call updateField directly
     */
    handleItemChange(item, newValue) {
      this.updateField(item.field, newValue);
    },

    /**
     * Handle collapse state change
     */
    handleCollapseChange(newValue) {
      this.isCollapsed = newValue;
      this.$emit("update:collapsed", newValue);
    },

    /**
     * Handle info icon click - emit onboarding event
     */
    handleOnboardingClick(onboardingKey) {
      if (onboardingKey) {
        this.$eventBus.$emit(onboardingKey);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.advanced-settings {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 16px;
  background: var(--color-surface-neutral-container-1, #f6f8fa);
  padding: 16px;
  border-radius: var(--radius-rd-400, 12px);

  &__group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__group-header {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 21px;

    .paragraph-s {
      color: var(--color-text-neutral-on-white, #1c0f30);
      font-weight: var(--typography-font-weight-fw-600, 600);
      line-height: 1.5;
    }
  }

  &__info-icon {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__item {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  &__item-label {
    display: flex;
    flex-direction: column;
    justify-content: center;

    .paragraph-s {
      color: var(--color-text-neutral-on-white, #1c0f30);
      line-height: 1.4;
    }
  }
}
</style>
